import { useTranslation } from "react-i18next";
import { Icon } from "@nexus/ui";
import { resolveRetentionNote } from "../lib/zero-state";

export interface BigQueryRetentionNoteProps {
  /** The window's resolved start, or undefined before the first read. */
  windowSince: string | undefined;
  /** `bigQueryRetentionDays` from the operations settings. */
  retentionDays: number;
}

/**
 * R24: a window that reaches back past the retention cutoff.
 *
 * On the page rather than inside the reconciliation dialog, because it explains
 * the chart and the tables — an empty stretch at the start of the window is rows
 * that have already been deleted, not traffic that stopped. Behind a button
 * nobody would open it to find that out, and the reading it corrects is a
 * reported collapse in usage.
 */
export function BigQueryRetentionNote({
  windowSince,
  retentionDays,
}: BigQueryRetentionNoteProps) {
  const { t, i18n } = useTranslation();

  const retention = resolveRetentionNote({
    windowStart: windowSince,
    retentionDays,
    now: new Date(),
  });

  if (!retention) return null;

  return (
    <section
      aria-label={t("admin.bigQueryUsage.reconciliation.retentionRegion", "Retention cutoff")}
      className="flex shrink-0 items-start gap-2 rounded-md border border-border bg-card px-3 py-2"
    >
      <Icon name="info" size="xs" color="muted" className="mt-0.5" aria-hidden />
      <p className="text-[11px] text-muted-foreground">
        {t(
          "admin.bigQueryUsage.reconciliation.retentionNote",
          "This window reaches back past the {{days}}-day retention cutoff of {{date}}. Rows older than that have already been deleted, so an empty stretch there is housekeeping rather than a drop in traffic.",
          {
            days: retention.retentionDays,
            date: new Date(retention.cutoffAt).toLocaleDateString(i18n.language, {
              month: "short",
              day: "numeric",
              year: "numeric",
            }),
          },
        )}
      </p>
    </section>
  );
}
